import styled from "styled-components";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table as UITable,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatToBRL } from "@/utils/formatToMoney";
import { getPagesToShow } from "@/utils/pagination";
import type { Meta } from "src/@types/paginated";

import type { INsuRow } from "src/@types/deposit-reconciliation";
import {
  formatNsuAdjustment,
  isNsuSelectable,
  nsuAmountClass,
  itemRowClass,
  nsuVariantOf,
} from "../utils/item-status";
import type { NsuFilterKey, NsuFilterValues } from "../types";
import { NSU_TABLE_COLUMN_COUNT, NSU_TABLE_HEADERS, TABLE_EMPTY_TEXT } from "../constants";
import ItemsTableBody from "./items-table-body";
import NsuFilters from "./nsu-filters";
import { ItemStatusBadge } from "./item-status-badge";
import PageSizeSelect from "../../components/page-size-select";

interface NsuTableProps {
  data: INsuRow[];
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  filters: NsuFilterValues;
  onFilterChange: (key: NsuFilterKey, value: string | undefined) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], checked: boolean) => void;
  periodStart?: string;
  periodEnd?: string;
}

export default function NsuTable({
  data,
  page,
  pageSize,
  total,
  isFetching,
  filters,
  onFilterChange,
  onPageChange,
  onPageSizeChange,
  selectedIds,
  onToggle,
  onToggleAll,
  periodStart,
  periodEnd,
}: NsuTableProps): JSX.Element {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const meta: Meta = {
    currentPage: page,
    lastPage,
    perPage: pageSize,
    total,
    prev: page > 1 ? page - 1 : null,
    next: page < lastPage ? page + 1 : null,
  };

  const selectableIds = data.filter(isNsuSelectable).map(n => n.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  return (
    <div className="border border-slate-200 rounded-md bg-white">
      <div className="border-b border-slate-200 p-3 bg-slate-50">
        <h3 className="font-semibold text-sm text-slate-700 mb-2">
          NSUs declaradas pela loja
          <span className="ml-2 text-slate-500 font-normal">({total})</span>
        </h3>
        <NsuFilters
          filters={filters}
          onFilterChange={onFilterChange}
          periodStart={periodStart}
          periodEnd={periodEnd}
        />
      </div>

      <UITable>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 text-center">
              <Checkbox
                checked={allSelected}
                onCheckedChange={c =>
                  onToggleAll(selectableIds, Boolean(c))
                }
                disabled={selectableIds.length === 0}
                aria-label="Selecionar todos"
              />
            </TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.NUMBER}</TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.OPERATOR}</TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.DATE}</TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.AMOUNT}</TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.ADJUSTED}</TableHead>
            <TableHead className="text-xs text-center">{NSU_TABLE_HEADERS.STATUS}</TableHead>
          </TableRow>
        </TableHeader>
        <ItemsTableBody
          isFetching={isFetching}
          isEmpty={data.length === 0}
          columnCount={NSU_TABLE_COLUMN_COUNT}
          emptyText={TABLE_EMPTY_TEXT.NSU}
        >
          {data.map(nsu => {
            const selectable = isNsuSelectable(nsu);
            const variant = nsuVariantOf(nsu);
            const rowClass = itemRowClass(
              nsu.isInDraft,
              selectable,
              selectedIds.has(nsu.id),
            );
            return (
              <TableRow key={nsu.id} className={rowClass}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(nsu.id)}
                    onCheckedChange={() => onToggle(nsu.id)}
                    disabled={!selectable}
                    aria-label={`Selecionar NSU ${nsu.number}`}
                  />
                </TableCell>
                <TableCell className="font-mono text-xs">{nsu.number}</TableCell>
                <TableCell className="text-xs">{nsu.operator}</TableCell>
                <TableCell className="text-xs">
                  {formatDateToDMY(nsu.conciliationDate.slice(0, 10))}
                </TableCell>
                <TableCell className={nsuAmountClass(nsu)}>
                  {formatToBRL(nsu.amount)}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">
                  {formatNsuAdjustment(nsu)}
                </TableCell>
                <TableCell>
                  <ItemStatusBadge
                    variant={variant}
                    batchId={nsu.batchId}
                    draftId={nsu.draftId}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </ItemsTableBody>
      </UITable>

      <div className="flex w-full items-center justify-between px-3 py-2 text-xs border-t border-slate-200">
        <PaginationButtonsContainer>
          {data.length !== 0 && (
            <>
              <button
                className={`navigate-btn previous ${
                  meta.prev !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.prev !== null && onPageChange(meta.prev)}
                disabled={meta.prev === null || isFetching}
              >
                Anterior
              </button>
              {getPagesToShow(meta).map((p, idx) => (
                <span
                  key={`${p}-${idx}`}
                  className={`pages ${p === meta.currentPage ? "selected" : ""} ${
                    p === "..." ? "ocult" : ""
                  }`}
                  onClick={() => typeof p === "number" && onPageChange(p)}
                >
                  {p}
                </span>
              ))}
              <button
                className={`navigate-btn next ${
                  meta.next !== null && !isFetching ? "" : "disabled"
                }`}
                onClick={() => meta.next !== null && onPageChange(meta.next)}
                disabled={meta.next === null || isFetching}
              >
                Próximo
              </button>
            </>
          )}
        </PaginationButtonsContainer>
        <div className="flex items-center gap-4">
          <PageSizeSelect value={pageSize} onChange={onPageSizeChange} />
          <span className="text-slate-600">
            Página {page} de {lastPage}
          </span>
        </div>
      </div>
    </div>
  );
}

const PaginationButtonsContainer = styled.div`
  display: flex;
  font-size: 14px;

  .pages {
    display: flex;
    justify-content: center;
    padding: 8px 6px;
    border: 1px solid #dcdce3;
    border-left: none;
    border-top: none;
    min-width: 29px;

    &.selected {
      background-color: #f4c83e;
    }
  }

  .pages:not(.ocult) {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .navigate-btn {
    cursor: pointer;
    padding: 6px 12px;
    background-color: #2b0c1a;
    color: #fff;

    &.previous {
      border-radius: 0 0 0 8px;
    }

    &.next {
      border-radius: 0 0 8px 0;
    }

    &:hover {
      text-decoration: underline;
    }

    &.disabled {
      color: #8d91a2;
      background-color: #d9dae0;
      cursor: not-allowed;
      text-decoration: none;
    }
  }
`;
